import { DAILY_PREMIUM_MODEL_LIMIT, CHAT_MODELS } from '../constants/freemium';

// =============================================================================
// Model Preference Storage
// =============================================================================

const MODEL_STORAGE_KEY = 'chatModelPreference';

/**
 * Get the user's selected chat model preference
 * @returns The model ID or null if not set
 */
export const getChatModelPreference = (): string | null => {
  try {
    return localStorage.getItem(MODEL_STORAGE_KEY);
  } catch {
    return null;
  }
};

/**
 * Set the user's chat model preference.
 * Selecting "default" clears the preference entirely so no model param
 * is sent to the backend (avoids stale model selections persisting).
 * @param modelId The model ID to set
 */
export const setChatModelPreference = (modelId: string): void => {
  try {
    if (modelId === 'default') {
      localStorage.removeItem(MODEL_STORAGE_KEY);
    } else {
      localStorage.setItem(MODEL_STORAGE_KEY, modelId);
    }
  } catch {
    // Ignore storage errors
  }
};

// =============================================================================
// Premium Model Usage Tracking
// =============================================================================

const USAGE_STORAGE_KEY = 'premiumModelUsage';

interface PremiumModelUsage {
  date: string; // ISO date string (YYYY-MM-DD)
  count: number;
}

/**
 * Get the current date as ISO string (YYYY-MM-DD) in user's local timezone
 * This matches the date format used in ChatPage for message count tracking.
 */
const getTodayDateString = (): string => {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), now.getDate()).toISOString().split('T')[0];
};

/**
 * Get the stored premium model usage, resetting if it's a new day
 */
const getStoredUsage = (): PremiumModelUsage => {
  try {
    const stored = localStorage.getItem(USAGE_STORAGE_KEY);
    if (stored) {
      const usage = JSON.parse(stored) as PremiumModelUsage;
      const today = getTodayDateString();
      // Reset count if it's a new day
      if (usage.date !== today) {
        return { date: today, count: 0 };
      }
      return usage;
    }
  } catch {
    // Ignore parse errors
  }
  return { date: getTodayDateString(), count: 0 };
};

/**
 * Save usage to localStorage
 */
const saveUsage = (usage: PremiumModelUsage): void => {
  try {
    localStorage.setItem(USAGE_STORAGE_KEY, JSON.stringify(usage));
  } catch {
    // Ignore storage errors
  }
};

/**
 * Get the current day's premium model usage
 * To test limits, edit localStorage directly:
 *   localStorage.setItem('premiumModelUsage', JSON.stringify({date: '2026-01-28', count: 19}))
 * @returns Object with used count and remaining count
 */
export const getPremiumModelUsage = (): { used: number; remaining: number } => {
  const usage = getStoredUsage();
  return {
    used: usage.count,
    remaining: Math.max(0, DAILY_PREMIUM_MODEL_LIMIT - usage.count),
  };
};

/**
 * Increment the premium model usage count
 * @returns true if successful, false if limit would be exceeded
 */
export const incrementPremiumModelUsage = (): boolean => {
  const usage = getStoredUsage();
  if (usage.count >= DAILY_PREMIUM_MODEL_LIMIT) {
    return false;
  }
  usage.count += 1;
  saveUsage(usage);
  return true;
};

/**
 * Decrement the premium model usage count (rollback on API failure)
 * Used to rollback optimistic usage increment when API call fails.
 */
export const decrementPremiumModelUsage = (): void => {
  const usage = getStoredUsage();
  if (usage.count > 0) {
    usage.count -= 1;
    saveUsage(usage);
  }
};

/**
 * Check if the user can still use premium models today
 * @returns true if under the daily limit
 */
export const canUsePremiumModel = (): boolean => {
  const { remaining } = getPremiumModelUsage();
  return remaining > 0;
};

/**
 * Check if a model requires subscription
 * @param modelId The model ID to check
 * @returns true if the model requires subscription
 */
export const isPremiumModel = (modelId: string): boolean => {
  const model = CHAT_MODELS.find((m) => m.id === modelId);
  return model?.requiresSubscription ?? false;
};
